import { LineStyle, PointStyle, Shadow } from "./poster-types";
import {
  PosterProject,
  PosterLegend,
  PosterLegendStyle,
  PosterProjectCurrentModelVersion,
  BorderStyle,
  MapLabelStyle,
  StaticLabelStyle,
  StaticLabel,
  MapLabel,
  MapLabelDecorationStyle,
  Backdrop,
  BackdropStyle,
  GPSSource,
  ProjectSize,
  Gallery,
  GalleryCell,
  Viewport,
} from "./poster-project";
import {
  EditingPermissions,
  PointEditingPermissions,
  BorderEditingPermissions,
  LineEditingPermissions,
  LabelEditingPermissions,
  BackdropEditingPermissions,
  GalleryEditingPermissions,
} from "./editing-permissions";
import { ProjectTemplate } from "./project-template";
import { v4 as uuid } from "uuid";
import { TemplateCollection } from "./template-collection";

export const defaultPointEditingPermissions: PointEditingPermissions = {
  size: true,
  icon: true,
  lineColor: true,
  fillColor: true,
  delete: true,
};

export const defaultBorderEditingPermissions: BorderEditingPermissions = {
  width: true,
  color: true,
};

export const defaultLineEditingPermissions: LineEditingPermissions = {
  thickness: true,
  color: true,
  opacity: true,
  dash: true,
  pointDensity: true,
  shadow: true,
};

export const defaultMapLabelEditingPermissions: LabelEditingPermissions = {
  position: true,
  text: true,
  font: true,
  size: true,
  color: true,
  alignment: true,
  backgroundColor: true,
  backgroundOpacity: true,
  backgroundPadding: true,
  delete: true,

  decoration: true,
  pointer: true,
};

export const defaultStaticLabelEditingPermissions: LabelEditingPermissions = {
  position: false,
  text: true,
  font: true,
  size: true,
  color: true,
  alignment: false,
  backgroundColor: false,
  backgroundOpacity: false,
  backgroundPadding: false,
  delete: false,

  decoration: false,
  pointer: false,
};

export const defaultBackdropEditingPermissions: BackdropEditingPermissions = {
  position: false,
  size: false,
  name: true,
  image: true,
  backgroundColor: false,
  backgroundOpacity: false,
  borderWidth: false,
  borderColor: false,
  borderRadius: false,
};

export const defaultGalleryEditingPermissions: GalleryEditingPermissions = {
  position: false,
  size: false,
  gap: false,
  orientation: false,
  addCell: false,
  removeCell: false,
  modifyCells: false,
  uploadImage: true,
  removeImage: true,
};

export const defaultViewport: Viewport = {
  id: "",
  position: { x: 0, y: 0 },
  size: { width: 1, height: 1 },
  imageID: "",
  contentOffset: { x: 0, y: 0 },
  contentScale: 1.0,
};

export const defaultGalleryCell: GalleryCell = {
  id: "",
  position: { x: 0, y: 0 },
  size: { width: 1, height: 1 },
  orientation: "LANDSCAPE",
  items: [],
};

export const defaultGallery: Gallery = {
  id: "",
  orientation: "LANDSCAPE",
  position: { x: 0, y: 0.5 },
  size: { width: 1, height: 0.5 },
  gap: 8,
  gapAroundEdges: false,
  gapTowardMap: true,
  backgroundColor: "#FFFFFF",
  backgroundOpacity: 0,
  content: [],
  editingPermissions: defaultGalleryEditingPermissions,
  isVisible: true,
};

export const defaultEditingPermissions: EditingPermissions = {
  mapStyle: true,
  mapPan: true,
  mapZoom: true,
  gpsSource: true,
  posterSize: true,
  line: defaultLineEditingPermissions,
  point: defaultPointEditingPermissions,
  border: defaultBorderEditingPermissions,
  mapLabel: defaultMapLabelEditingPermissions,
  staticLabel: defaultStaticLabelEditingPermissions,
  backdrop: defaultBackdropEditingPermissions,
  gallery: defaultGalleryEditingPermissions,
};

export const defaultShadow: Shadow = {
  color: "rgba(0, 0, 0, 0.5)",
  opacity: 0.5,
  blur: 10,
  offset: { width: 0, height: 10 },
  isHidden: false,
};

export const defaultLineStyle: LineStyle = {
  width: 5,
  mainColor: "black",
  flankColor: "white",
  mainPattern: "doubleTranslucent",
  dotDash: false,
  shadow: defaultShadow,
};

const defaultPointStyle: PointStyle = {
  showPoint: true,
  size: 40,
  mainColor: "black",
  secondaryColor: "white",
  icon: "DOT",
};

const defaultLegendStyle: PosterLegendStyle = {
  visible: false,
  labelColor: "#000000",
  backgroundColor: "#FFFFFF",
  backgroundOpacity: 1.0,
  position: "bottom",
  labelTemplate: "layout_01",
};

const defaultLegend: PosterLegend = {
  labels: ["Title", "Click labels to edit"],
  style: defaultLegendStyle,
};

const defaultBorderStyle: BorderStyle = {
  borderWidth: 10,
  borderColor: "#FFFFFF",
};

export const defaultDecorationStyle: MapLabelDecorationStyle = {
  decoration: "line",
  width: 1,
  length: 1,
  color: "black",
};

export const defaultLabelStyle: MapLabelStyle = {
  showImage: true,
  showText: true,
  backgroundColor: "white",
  backgroundOpacity: 0.5,
  padding: 8,
  showPointer: true,
  pointerColor: "white",
  pointerThickness: 4,
  decorationStyle: defaultDecorationStyle,
  font: "Arial",
  textColor: "black",
  fontSize: 20,
  offset: { x: 50, y: -50 },
  borderRadius: 20,
  borderWidth: 20,
  borderColor: "white",
  imageSize: 30,
  isUppercase: false,
  shadow: defaultShadow,
  alignment: "center",
  lineSpacing: 100,
};

export const DefaultMapLabel: MapLabel = {
  id: "",
  pointID: "",
  type: "text",
  coordinate: { lat: 0, lng: 0 },
  text: "Label",
  style: defaultLabelStyle,
  editingPermissions: defaultMapLabelEditingPermissions,
};

export const defaultStaticLabelStyle: StaticLabelStyle = {
  alignment: "left",
  lineSpacing: 100,
  backgroundColor: "white",
  backgroundOpacity: 0.0,
  padding: 0,
  borderRadius: 0,
  font: "FrancoisOne",
  textColor: "black",
  fontSize: 30,
  isUppercase: false,
  isHidden: false,
};

export const DefaultStaticLabel: StaticLabel = {
  id: "",
  description: "Label",
  position: { x: 0, y: 0 },
  text: "Label",
  style: defaultStaticLabelStyle,
  editingPermissions: defaultStaticLabelEditingPermissions,
};

export const defaultBackdropStyle: BackdropStyle = {
  backgroundColor: "white",
  foregroundColor: "black",
  opacity: 0.5,
  foregroundOpacity: 1,
  borderColor: "white",
  borderWidth: 2,
  cornerRadius: 3,
  padding: 0,
};

export const defaultBackdrop: Backdrop = {
  id: "",
  position: { x: 0.3, y: 0.3 },
  size: { width: 0.1, height: 0.2 },
  style: defaultBackdropStyle,
  name: "Backdrop 1",
  editingPermissions: defaultBackdropEditingPermissions,
  type: "color",
};

export const defaultGPSSource: GPSSource = {
  id: "",
  name: "",
  originDescription: "",
  allCoordinates: [],
  displayedCoordinates: [],
  coordinateLimit: 0,
  data: undefined,
};

export const defaultSizeOption: ProjectSize = {
  presetID: "fcc7bf6f-242e-4cb3-bfff-330c46ca227e",
  options: {
    orientation: "landscape",
  },
};

export const defaultProject: PosterProject = {
  id: uuid(),
  name: "New Project",
  modelVersion: PosterProjectCurrentModelVersion,
  createdAt: new Date(),
  updatedAt: new Date(),
  lastRenderID: undefined,
  isRendered: false,

  editingPermissions: defaultEditingPermissions,

  isTemplateDraft: false,
  isInCheckout: false,
  isOrdered: false,

  gpsSourceIDs: [],
  gpsData: undefined,

  sizeOption: defaultSizeOption,
  size: "12x12",

  mapStyle: "mapbox://styles/jamesbkelly/cm4tpl8w3002101qvhtnp3wa8",
  lineStyle: defaultLineStyle,
  pointStyle: defaultPointStyle,
  legendStyle: defaultLegendStyle,
  borderStyle: defaultBorderStyle,
  mapLabelStyle: defaultLabelStyle,
  staticLabelStyle: defaultStaticLabelStyle,
  backdropStyle: defaultBackdropStyle,
  legend: defaultLegend,
  styledPoints: [],

  mapLabels: [],
  staticLabels: [],
  backdrops: [],
};

export const defaultProjectTemplate: ProjectTemplate = {
  id: "",
  createdAt: new Date(),
  updatedAt: new Date(),
  thumbnail: "",
  project: defaultProject,
  name: "New Template",
  description: "",
  appliedElements: {
    posterSize: true,
    mapStyle: true,
    mapCoordinates: true,
    borderStyle: true,
    pointStyle: true,
    lineStyle: true,
    mapLabelStyle: true,
    staticLabelStyle: true,
    backdropStyle: true,

    routeCoordinates: false,
    styledPoints: false,
    backdrops: true,
    mapLabels: false,
    staticLabels: true,
    gallery: true,
  },
  editingPermissions: defaultEditingPermissions,
};

export const defaultTemplateCollection: TemplateCollection = {
  id: "",
  createdAt: new Date(),
  updatedAt: new Date(),
  createdByID: "",
  name: "New Collection",
  description: "",
  templateIDs: [],
};
